
import { Vue, Component, Ref, Watch } from 'vue-property-decorator';
import SalesOrderDetailModal from './components/SalesOrderDetailModal.vue';
import ConfirmPaymentModal from './components/ConfirmPaymentModal.vue';
import SaleOrderInvoiceModal from './components/SaleOrderInvoiceModal.vue';
import lazyInject from '@/di';
import SupplyService from '@/services/supply.service';
import { CSaleOrderList } from '@/models/supply.model';
import moment from 'moment';
import { AuthEnum } from '@/services/permission.service';

@Component({
  components: {
    SalesOrderDetailModal,
    ConfirmPaymentModal,
    SaleOrderInvoiceModal,
  },
})
export default class PlatformSalesOrder extends Vue {
  loading = false;

  AuthEnum = AuthEnum;

  dataSource: any = [];

  filterForm: any = {
    order_no: '',
    distributor_code: undefined,
    commodity_name: '',
    // 下单日期默认最近一个月
    start_time: moment().subtract(1, 'months').format('YYYY-MM-DD'),
    end_time: moment().format('YYYY-MM-DD'),
    batch_no: undefined,
    po: '',
    pay_status: undefined,
    amap_pay_status: undefined,
    shipment_status: undefined,
    invoice_status: undefined,
  };

  distributorOptions: any[] = [];

  batchOptions: string[] = [];

  // 0-未支付，1-已支付(待收款)，2-已确认收款
  payStatusOptions = [
    { label: '待付款', value: 0 },
    { label: '待收款', value: 1 },
    { label: '已收款', value: 2 },
  ];

  // 1-待归集，1-待请款，2-请款中，3-已到账，4-已付款
  amapPayStatusOptions = [
    { label: '待归集', value: 1 },
    { label: '待请款', value: 2 },
    { label: '已请款', value: 3 },
  ];

  // 0-待发货，1-在途，2-已签收
  shipmentStatusOptions = [
    { label: '待发货', value: 2 },
    { label: '已发货', value: 3 },
    { label: '已签收', value: 4 },
  ];

  // 0-未开票
  invoiceStatusOptions = [
    { label: '未开票', value: 0 },
    { label: '已开票', value: 1 },
  ];

  @lazyInject(SupplyService)
  supplyService!: SupplyService;

  columns = [
    {
      title: '订单编号',
      dataIndex: 'orderNo',
      align: 'center',
    },
    {
      title: '经销商名称',
      dataIndex: 'distributorName',
      align: 'center',
    },
    {
      title: '省份',
      dataIndex: 'province',
      align: 'center',
    },
    {
      title: '总壶数',
      dataIndex: 'quantity',
      align: 'center',
    },
    {
      title: '总金额',
      dataIndex: 'amount',
      align: 'center',
    },
    {
      title: '付款状态',
      dataIndex: 'payStatus',
      align: 'center',
      scopedSlots: { customRender: 'payStatus' },
    },
    {
      title: '请款批次',
      dataIndex: 'batchNo',
      align: 'center',
    },
    {
      title: '付款PO',
      dataIndex: 'po',
      align: 'center',
    },
    {
      title: '高德付款状态',
      dataIndex: 'amapPayStatus',
      align: 'center',
    },
    {
      title: '物流状态',
      dataIndex: 'shipmentStatus',
      align: 'center',
    },
    {
      title: '开票状态',
      dataIndex: 'invoiceStatus',
      align: 'center',
    },
    {
      title: '操作',
      dataIndex: 'action',
      align: 'center',
      fixed: 'right',
      width: 200,
      scopedSlots: { customRender: 'action' },
    },
  ];

  pagination: any = {
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: this.$store.state.pageSize.pageSize,
    showTotal: () => `共${this.pagination.total}条`,
    onChange: this.paginationChange,
    onShowSizeChange: this.onShowSizeChange,
  };

  @Watch('$route')
  async handleSearch() {
    const {
      order_no,
      distributor_code,
      commodity_name,
      start_time,
      end_time,
      batch_no,
      po,
      pay_status,
      amap_pay_status,
      shipment_status,
      invoice_status,
      page,
      pageSize,
    } = this.$route.query;
    const params: any = { page: page || 1, pageSize: pageSize || 10 };
    if (order_no) {
      params.order_no = order_no;
      this.filterForm.order_no = order_no;
    }
    if (distributor_code) {
      params.distributor_code = distributor_code;
      this.filterForm.distributor_code = distributor_code;
    }
    if (commodity_name) {
      params.commodity_name = commodity_name;
      this.filterForm.commodity_name = commodity_name;
    }
    if (start_time && end_time) {
      params.start_time = start_time;
      params.end_time = end_time;
      this.filterForm.start_time = start_time;
      this.filterForm.end_time = end_time;
    }
    if (batch_no) {
      params.batch_no = batch_no;
      this.filterForm.batch_no = batch_no;
    }
    if (po) {
      this.filterForm.po = po;
      params.po = po;
    }
    if (pay_status !== undefined) {
      params.pay_status = pay_status;
      this.filterForm.pay_status = +pay_status;
    }
    if (amap_pay_status !== undefined) {
      params.amap_pay_status = amap_pay_status;
      this.filterForm.amap_pay_status = +amap_pay_status;
    }
    if (shipment_status !== undefined) {
      params.shipment_status = shipment_status;
      this.filterForm.shipment_status = +shipment_status;
    }
    if (invoice_status !== undefined) {
      this.filterForm.invoice_status = +invoice_status;
      params.invoice_status = invoice_status;
    }
    this.getList(params);
  }

  mounted() {
    this.handleSearch();
    this.loadDistributorOptions();
    // this.loadBatchOptions();
  }

  async getList(params: any) {
    // TODO: 调用平台销售单列表接口，参数：this.pagination.current
    const res = await this.supplyService.getSaleOrderList(params, this.$el);
    this.dataSource = res.list.map((item: any) => CSaleOrderList.from(item));
    // console.log(this.dataSource);
    this.pagination.total = res.total;
    this.pagination.current = res.page;
    this.pagination.pageSize = res.pageSize;
  }

  search() {
    this.pagination.current = 1;
    this.getQueryParams();
  }

  resetQuery() {
    this.filterForm = {
      order_no: '',
      distributor_code: undefined,
      commodity_name: '',
      start_time: moment().subtract(1, 'months').format('YYYY-MM-DD'),
      end_time: moment().format('YYYY-MM-DD'),
      batch_no: undefined,
      po: '',
      pay_status: undefined,
      amap_pay_status: undefined,
      shipment_status: undefined,
      invoice_status: undefined,
    };
    this.pagination.current = 1;
    this.getQueryParams();
  }

  formatDate(date: any) {
    return date ? moment(date).format('YYYY-MM-DD') : '';
  }

  onChangeOrderDate(dates: any) {
    this.filterForm.start_time = this.formatDate(dates && dates[0]);
    this.filterForm.end_time = this.formatDate(dates && dates[1]);
  }

  // 经销商下拉：仅取状态【合作中】的经销商；模糊搜索单独调接口按 company_name 筛选
  async loadDistributorOptions(companyName = '') {
    const res: any = await this.supplyService.getDistributorSearch({
      company_name: companyName,
    });
    const list = res || [];
    this.distributorOptions = list.map((item: any) => ({
      label: item.name,
      value: item.sd_code,
    }));
  }

  onDistributorSearch(companyName: string) {
    this.loadDistributorOptions(companyName);
  }

  // 请款批次下拉：拉取全部批次，showSearch 前端按关键字关联
  async loadBatchOptions() {
    const res: any = await this.supplyService.getPurchaseOrderList({
      page: 1,
      pageSize: 999,
    });
    const list = (res && res.list) || [];
    const batches: string[] = [];
    list.forEach((item: any) => {
      const no = item.batchNo || item.batch_no;
      if (no && batches.indexOf(no) === -1) {
        batches.push(no);
      }
    });
    this.batchOptions = batches;
  }

  getQueryParams() {
    const {
      order_no,
      distributor_code,
      commodity_name,
      start_time,
      end_time,
      batch_no,
      po,
      pay_status,
      amap_pay_status,
      shipment_status,
      invoice_status,
    } = this.filterForm;
    const { current, pageSize } = this.pagination;
    const query: any = { page: current, pageSize };
    if (order_no) {
      query.order_no = order_no;
    }
    if (distributor_code) {
      query.distributor_code = distributor_code;
    }
    if (commodity_name) {
      query.commodity_name = commodity_name;
    }
    if (start_time && end_time) {
      query.start_time = start_time;
      query.end_time = end_time;
    }
    if (batch_no) {
      query.batch_no = batch_no;
    }
    if (po) {
      query.po = po;
    }
    if (pay_status !== undefined) {
      query.pay_status = pay_status;
    }
    if (amap_pay_status !== undefined) {
      query.amap_pay_status = amap_pay_status;
    }
    if (shipment_status !== undefined) {
      query.shipment_status = shipment_status;
    }
    if (invoice_status !== undefined) {
      query.invoice_status = invoice_status;
    }
    query.timestamp = Math.round(Date.now() / 1000);
    this.$router.push({
      query,
    });
  }

  paginationChange(page: number, pageSize: number) {
    this.pagination.current = page;
    this.pagination.pageSize = pageSize;
    this.getQueryParams();
  }

  onShowSizeChange(current: number, size: number) {
    this.pagination.current = 1;
    this.pagination.pageSize = size;
    this.getQueryParams();
  }

  viewDetail(record: any) {
    this.salesOrderDetailModal.open(record);
  }

  confirmPayment(record: any) {
    this.confirmPaymentModal.open(record);
  }

  uploadInvoice(record: any) {
    this.saleOrderInvoiceModal.open(record);
  }

  @Ref('salesOrderDetailModal')
  salesOrderDetailModal!: SalesOrderDetailModal;

  @Ref('confirmPaymentModal')
  confirmPaymentModal!: ConfirmPaymentModal;

  @Ref('saleOrderInvoiceModal')
  saleOrderInvoiceModal!: SaleOrderInvoiceModal;
}
